import { useRef, useEffect } from 'react';
import { useSoftphoneCallState } from '../providers/softphone-call-state-provider';
import { beepAudio, ringAudio } from './audio/ring-audio';

// Keep this outside of the component to avoid re-creating the interval on every render
let beepAudioInterval: NodeJS.Timeout | null = null;

export const useSoftphoneRingManager = () => {
  /**
   * CONTEXT
   *
   * This manager works on the assumption that the incoming call queue is in a PRE-PROCESSED state.
   * This means that incoming calls have yet to be removed from the list - if it were going to be processed (answered) in the current loop.
   */
  const incomingCalls = useSoftphoneCallState((ctx) => ctx.incomingCalls);
  const establishedCalls = useSoftphoneCallState((ctx) => ctx.establishedCalls);
  const outgoingCalls = useSoftphoneCallState((ctx) => ctx.outgoingCalls);

  //storing this stuff in refs so I don't have to keep re-subscribing to events whenever these change
  const incomingCallsRef = useRef(incomingCalls);
  const establishedCallsRef = useRef(establishedCalls);
  const outgoingCallsRef = useRef(outgoingCalls);

  useEffect(() => {
    incomingCallsRef.current = incomingCalls;
    establishedCallsRef.current = establishedCalls;
    outgoingCallsRef.current = outgoingCalls;
  }, [establishedCalls, incomingCalls, outgoingCalls]);

  useEffect(() => {
    return () => {
      if (beepAudioInterval) {
        clearInterval(beepAudioInterval);
        beepAudioInterval = null;
      }
    };
  }, []);

  const playRing = () => {
    if (ringAudio.paused) {
      ringAudio.play();
    }
  };

  const playBeep = () => {
    if (beepAudio.paused && !beepAudioInterval) {
      beepAudio.play();

      beepAudioInterval = setInterval(() => {
        beepAudio.play();
      }, 7000);
    }
  };

  const stopRing = () => {
    ringAudio.pause();
  };

  const stopBeep = () => {
    if (beepAudioInterval) {
      beepAudio.pause();
      clearInterval(beepAudioInterval);
      beepAudioInterval = null;
    }
  };

  const isOnlyCallInQueue = (callId: string) =>
    incomingCallsRef.current.length === 1 && incomingCallsRef.current[0].id === callId;

  const handleIncomingRing = () => {
    if (establishedCallsRef.current?.length > 0 || outgoingCallsRef.current?.length > 0) {
      playBeep();
    } else if (establishedCallsRef.current.length === 0) {
      playRing();
    }
  };

  const handleAnswerRing = () => {
    stopRing();
    if (incomingCallsRef.current.length > 1) {
      playBeep();
    } else {
      stopBeep();
    }
  };

  const stopAllRing = () => {
    stopBeep();
    stopRing();
  };

  const handleRejectRing = (callId: string) => {
    // We only care to do something if there's only one call in the queue
    if (isOnlyCallInQueue(callId)) {
      stopBeep();
      stopRing();
    }
  };

  const handleMissedRing = (callId: string) => {
    // We only care to do something if there's only one call in the queue
    if (isOnlyCallInQueue(callId)) {
      stopRing();
      stopBeep();
    }
  };

  const handleCompletedRing = () => {
    if (incomingCallsRef.current.length > 0) {
      stopBeep();
      playRing();
    }
  };

  return {
    stopAllRing,
    handleIncomingRing,
    handleAnswerRing,
    handleRejectRing,
    handleMissedRing,
    handleCompletedRing,
  };
};
